import React, { CSSProperties, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { LinkModel } from '@/types';
import ResponsiveImage, { IResponsiveImageProps } from '../general/ResponsiveImage';
import classNames from 'classnames';
import { openLink } from '@/utils';
import { useRouter } from 'next/router';
import {
  AppInsightsContext,
  TrackEventKey,
  TrackEventName,
  TrackEventType,
} from '../azureAppInsights/AzureAppInsightsContext';
import { MetadataContext, SystemFeaturesContext, UserContext } from '../ContextProviders';
import { CopyTextBtn, MediaGalleryContext, Tooltip } from '..';
import DownloadSimpleLight from '@/assets/downloadSimpleLight.svg';
import { useTranslations } from 'next-intl';
import { downloadImage, saveToScrapbook } from '@/api';
import FolderSelectionDialog from '../scrapbook/dialogs/FolderSelectionDialog';
import ScissorsLight from '@/assets/scissors-light.svg';
import { useNotification } from '../general/NotificationProvider';

export interface IGeneralImageButtonProps {
  icon: React.ReactNode;
  tooltipText: string;
  onClick: (e: React.MouseEvent) => void;
}

export interface IGeneralImageProps extends IResponsiveImageProps {
  targetImageUrl?: LinkModel;
  setContanerSize?: boolean;
  showCopyrightText?: boolean;
  showCopyBtns?: boolean;
  disableClick?: boolean;
  buttons?: IGeneralImageButtonProps[];
  showDownloadBtn?: boolean;
}

const GeneralImage = ({
  image,
  targetImageUrl,
  setContanerSize,
  showCopyrightText,
  disableClick = false,
  className,
  imgProps,
  buttons,
  showCopyBtns,
  showDownloadBtn,
  ...props
}: IGeneralImageProps) => {
  const router = useRouter();

  const [contanerStyle, setContanerStyle] = useState<CSSProperties>();
  const [showFolderDialog, setShowFolderDialog] = useState(false);

  const appInsights = useContext(AppInsightsContext);
  const mediaGalleryContext = useContext(MediaGalleryContext);
  const user = useContext(UserContext);
  const { pageId, pageType, pageTitle, pageUrl } = useContext(MetadataContext);
  const { saveToScrapbook: saveToScrapbookFeature } = useContext(SystemFeaturesContext);
  const t = useTranslations();
  const { showNotification } = useNotification();

  const currentElement = useRef<HTMLDivElement>(null);

  useEffect(() => {
    calcContanerSize();
    const onResize = () => {
      if (setContanerSize) {
        calcContanerSize();
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClick = () => {
    if (disableClick) return;
    if (targetImageUrl) {
      openLink(router, targetImageUrl);
    } else {
      mediaGalleryContext.openMediaGallery(image);
      appInsights.trackGalleryEvent(TrackEventType.OPEN, user, { pageId });
    }
  };

  const calcContanerSize = async () => {
    if (setContanerSize) {
      const imageElement = currentElement.current?.querySelector('img');

      if (!imageElement) return;
      if (!imageElement.complete) {
        await new Promise<void>((resolve) => {
          imageElement.onload = () => resolve();
          imageElement.onerror = () => resolve();
        });
      } else if (imageElement.decode) {
        try {
          await imageElement.decode();
        } catch {
          // ignore decode errors, continue with fallback
        }
      }

      setContanerStyle({});

      setTimeout(() => {
        const calculatedHeight = (imageElement.width * imageElement.naturalHeight) / imageElement.naturalWidth;
        const calculatedWidth = (imageElement.height * imageElement.naturalWidth) / imageElement.naturalHeight;
        if (!calculatedWidth || !calculatedHeight) return;

        setContanerStyle({
          width: `${calculatedWidth}px`,
          height: `${calculatedHeight}px`,
        });
      }, 100);
    }
  };

  const isReportPage = useMemo(() => {
    return pageType === 'legacyReportPage' || pageType === 'reportPage';
  }, [pageType]);

  const buttonsList: IGeneralImageButtonProps[] = useMemo(() => {
    const _buttons: IGeneralImageButtonProps[] = [...(buttons || [])];

    if ((isReportPage || showDownloadBtn) && (user.isAuthenticated || user.isIpBasedIdentity)) {
      _buttons.push({
        icon: <DownloadSimpleLight className="h-6 w-6 !fill-black" />,
        tooltipText: t('general.$download'),
        onClick: (e) => {
          e.stopPropagation();
          appInsights.trackCustomEvent(TrackEventName.PAGE, TrackEventKey.IMAGE, TrackEventType.DOWNLOAD, user, {
            pageId,
            pagePath: router.asPath,
            imageID: image.mediaId,
          });
          downloadImage(image);
        },
      });

      if (!user.isIpBasedIdentity && saveToScrapbookFeature) {
        _buttons.push({
          icon: <ScissorsLight className="h-6 w-6 !fill-black" />,
          tooltipText: 'Save to Scrapbook',
          onClick: (e) => {
            e.stopPropagation();
            appInsights.trackCustomEvent(
              TrackEventName.PAGE,
              TrackEventKey.IMAGE,
              TrackEventType.SAVE_TO_SCRAPBOOK,
              user,
              {
                pageId,
                pagePath: router.asPath,
                imageID: image.mediaId,
              },
            );
            setShowFolderDialog(true);
          },
        });
      }
    }
    return _buttons;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttons, user.isAuthenticated, isReportPage, user.isIpBasedIdentity, image.mediaId, pageId, router.asPath]);

  return (
    <div
      ref={currentElement}
      className={classNames('general-image-container flex h-full w-full items-center justify-center', className)}
    >
      <div
        className={classNames(
          'general-image group relative flex break-inside-avoid flex-col',
          showCopyrightText && image.copyrightText ? 'mb-5 h-[calc(100%-20px)]' : 'h-full',
          setContanerSize && '[&_img]:!w-auto',
          !disableClick && 'cursor-pointer',
        )}
        style={contanerStyle}
        onClick={onClick}
      >
        <ResponsiveImage
          image={image}
          className="relative block min-h-full w-full"
          imgProps={{ ...imgProps }}
          {...props}
        />
        {buttonsList && buttonsList.length > 0 && (
          <div className="buttons absolute top-0 right-0 flex flex-col gap-3 pt-3 pr-3">
            {buttonsList.map((button, index) => (
              <Tooltip key={index} content={button.tooltipText} placement="left">
                <div
                  className="btn icon cursor-pointer rounded-full p-2 shadow-md transition-all group-hover:opacity-100 pointer-fine:opacity-0"
                  style={{ transitionDelay: `${index * 10}ms` }}
                  onClick={button.onClick}
                >
                  {button.icon}
                </div>
              </Tooltip>
            ))}
          </div>
        )}
        <div className="caption absolute bottom-0 left-0 mb-1 px-2 text-xs leading-[18px] font-bold tracking-[0.4px] text-white text-shadow-[0_0_10px_rgb(0_0_0)]">
          <CopyTextBtn copyText={image.caption} active={showCopyBtns}>
            {image.caption}
          </CopyTextBtn>
        </div>
        {showCopyrightText && image.copyrightText && (
          <div className="copyright p-1 text-right text-xs leading-[18px] font-bold whitespace-nowrap text-black">
            <div className="flex justify-end">
              <CopyTextBtn copyText={image.copyrightText} placement="right" active={showCopyBtns}>
                {image.copyrightText}
              </CopyTextBtn>
            </div>
          </div>
        )}
      </div>
      <FolderSelectionDialog
        isOpen={showFolderDialog}
        onClose={() => setShowFolderDialog(false)}
        onSave={async (folderId: number) => {
          try {
            const content = {
              imageId: image.mediaId,
              imageUrl: image.imageUrl,
              imageCaption: image.caption,
              imageCopyright: image.copyrightText,
              pageId: pageId,
              pageUrl: pageUrl,
              pageTitle: pageTitle,
            };

            const result = await saveToScrapbook(
              folderId,
              {
                path: `img.${image.mediaId}.json`,
                contentType: 'application/x.stylus.lnk.img+json',
                content: JSON.stringify(content),
              },
              user?.userId,
            );

            if (result?.succeeded) {
              showNotification(result.message || 'Image saved to scrapbook successfully', 'savesuccess');
            } else {
              showNotification('Call to scrapbook server failed.', 'error');
            }
          } catch {
            showNotification('Call to scrapbook server failed.', 'error');
          }
        }}
        reportTitle={pageTitle || image.caption || ''}
      />
    </div>
  );
};

export default GeneralImage;
